import { useCallback, useRef, useState } from 'react';

import { chartLayout } from '../config/chartLayout';
import { lineDrawingBudget } from '../config/chartVisuals';

/**
 * The plot width for a container width, rounded to a coarse step so that dragging a window edge
 * does not re-thin every line on each pixel of the resize.
 */
export function quantizePlotWidth(containerWidthPx: number): number {
  const plotWidthPx = Math.max(0, containerWidthPx - chartLayout.gridSideInset * 2);

  return Math.round(plotWidthPx / lineDrawingBudget.widthStepPx) * lineDrawingBudget.widthStepPx;
}

/**
 * The plot's width in pixels, for the drawing budget and symbol visibility. Attach the returned ref
 * to the element that sizes the chart. Where the size cannot be observed (no ResizeObserver, as in
 * jsdom) the configured fallback width stays.
 */
export function usePlotWidth() {
  const [plotWidthPx, setPlotWidthPx] = useState<number>(lineDrawingBudget.fallbackPlotWidthPx);
  const observerRef = useRef<ResizeObserver | undefined>(undefined);

  const containerRef = useCallback((element: HTMLElement | null) => {
    observerRef.current?.disconnect();
    observerRef.current = undefined;

    if (!element || typeof ResizeObserver === 'undefined') {
      return;
    }

    const observer = new ResizeObserver(([entry]) => {
      if (entry) {
        setPlotWidthPx(quantizePlotWidth(entry.contentRect.width));
      }
    });

    observer.observe(element);
    observerRef.current = observer;
  }, []);

  return { containerRef, plotWidthPx };
}
